import { useState } from 'react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { TournamentMatchDocument, CollectionPaths } from 'shared_schemas';

interface Props {
    open: boolean;
    match: TournamentMatchDocument;
    onClose: () => void;
}

export function MatchReportModal({ open, match, onClose }: Props) {
    const [p1Score, setP1Score] = useState(match.player1Score || 0);
    const [p2Score, setP2Score] = useState(match.player2Score || 0);
    const [saving, setSaving] = useState(false);

    const handleSave = async (complete: boolean) => {
        setSaving(true);
        try {
            const updates: any = {
                player1Score: p1Score,
                player2Score: p2Score,
                updatedAt: Timestamp.now()
            };

            if (complete) {
                updates.status = 'completed';
                updates.completedAt = Timestamp.now();
                if (p1Score > p2Score) updates.winnerId = match.player1Id || 'p1'; // Fallback if no ID
                else if (p2Score > p1Score) updates.winnerId = match.player2Id || 'p2';
            } else {
                updates.status = 'in_progress';
            }

            await updateDoc(doc(db, CollectionPaths.tournamentMatches, match.id), updates);
            onClose();
        } catch (err) {
            console.error("Failed to report score", err);
            alert("Failed to save score");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-secondary-800 rounded-xl border border-secondary-700 shadow-2xl w-full max-w-md overflow-hidden">
                <div className="p-6">
                    <h2 className="text-xl font-bold text-white mb-1">Report Score</h2>
                    <p className="text-secondary-400 text-sm mb-6">{match.fullRoundText} • Match {match.startggIdentifier}</p>

                    <div className="space-y-6">
                        {/* Player 1 */}
                        <div className="flex items-center justify-between">
                            <span className="font-medium text-white text-lg truncate w-1/2">{match.player1Name}</span>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setP1Score(Math.max(0, p1Score - 1))}
                                    className="w-8 h-8 rounded bg-secondary-700 text-white hover:bg-secondary-600"
                                >-</button>
                                <span className="text-2xl font-mono font-bold w-6 text-center">{p1Score}</span>
                                <button
                                    onClick={() => setP1Score(p1Score + 1)}
                                    className="w-8 h-8 rounded bg-secondary-700 text-white hover:bg-secondary-600"
                                >+</button>
                            </div>
                        </div>

                        {/* Player 2 */}
                        <div className="flex items-center justify-between">
                            <span className="font-medium text-white text-lg truncate w-1/2">{match.player2Name}</span>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setP2Score(Math.max(0, p2Score - 1))}
                                    className="w-8 h-8 rounded bg-secondary-700 text-white hover:bg-secondary-600"
                                >-</button>
                                <span className="text-2xl font-mono font-bold w-6 text-center">{p2Score}</span>
                                <button
                                    onClick={() => setP2Score(p2Score + 1)}
                                    className="w-8 h-8 rounded bg-secondary-700 text-white hover:bg-secondary-600"
                                >+</button>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex gap-3">
                        <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
                        <button
                            onClick={() => handleSave(false)}
                            className="btn-secondary border-primary-500/50 text-white flex-1"
                            disabled={saving}
                        >
                            Update Live
                        </button>
                        <button
                            onClick={() => handleSave(true)}
                            className="btn-primary flex-1 bg-green-600 hover:bg-green-500 border-none"
                            disabled={saving}
                        >
                            Final Result
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
